import { useRollbar } from '@rollbar/react'
import {
  deleteCommonBlock,
  deleteMasterBlock,
  deleteUserBlock,
} from 'client/actions/blockActions'
import { createBlockAtTheBottom } from 'client/actions/entityActions'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import useManagement from 'client/hooks/useManagement'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import usePresentPage, {
  selectors as pageSelectors,
} from 'client/hooks/usePresentPage'
import { isUserAdmin } from 'client/reducers/managementReducer'
import { getPageTypeEntities } from 'common/constants/pageTypeEntitiesMap'
import { useActions } from 'common/hooks/useActions'
import React, { useCallback, useMemo, useState } from 'react'
import { BackButton } from 'src/editor/shared/components/back-button'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { blockCategories } from '../consts'
import { BlockCategoryType, BlockType } from '../types'
import { Block } from './block'

interface BlocksListingProps {
  activeCategory: BlockCategoryType | null
  setActiveCategory: React.Dispatch<
    React.SetStateAction<BlockCategoryType | null>
  >
}

export const BlocksListing = ({
  activeCategory,
  setActiveCategory,
}: BlocksListingProps) => {
  const { t } = useTypedTranslation()
  const { isMobile } = useDeviceModeContext()
  const rollbar = useRollbar()
  const { isTemplate } = usePageRouteDetector()
  const pageType = usePresentPage(pageSelectors.getPageType)

  const commonBlocksByCategories: Record<BlockCategoryType, BlockType[]> =
    useBlocks(blocksSelectors.getCommonBlocksByCategories)
  const userBlocks: BlockType[] = useBlocks(blocksSelectors.getUserBlocks)
  const masterBlocks: BlockType[] = useBlocks(blocksSelectors.getMasterBlocks)
  const usedMasterBlockIds = usePresentPage(pageSelectors.getUsedMasterBlockIds)
  const allowedPageEntities = getPageTypeEntities(pageType)

  const deleteUserBlockAction = useActions(deleteUserBlock)
  const deleteMasterBlockAction = useActions(deleteMasterBlock)
  const addToTheBottom = useActions(createBlockAtTheBottom)
  const removeCommonBlock = useActions(deleteCommonBlock)

  const [searchValue, setSearchValue] = useState('')
  const isAdmin = useManagement(isUserAdmin)

  const isMasterBlockInUse = useCallback(
    (block: BlockType) =>
      block.isMaster && usedMasterBlockIds.includes(block.id),
    [usedMasterBlockIds],
  )

  const filterBySearchValue = useCallback((searchValue: string) => {
    return (filterable: { title: string }) => {
      return !searchValue.length || filterable.title.indexOf(searchValue) > -1
    }
  }, [])

  const isCustomizableCategory = useMemo(
    () =>
      (!isTemplate && activeCategory === blockCategories.userMaster) ||
      activeCategory === blockCategories.userSimple,
    [isTemplate, activeCategory],
  )

  const renderedBlocks = useMemo(() => {
    if (activeCategory === blockCategories.userSimple) {
      return userBlocks.filter(filterBySearchValue(searchValue))
    } else if (!isTemplate && activeCategory === blockCategories.userMaster) {
      return masterBlocks.filter(filterBySearchValue(searchValue))
    } else if (activeCategory && activeCategory !== blockCategories.user) {
      return commonBlocksByCategories[activeCategory]
    } else {
      return []
    }
  }, [
    searchValue,
    activeCategory,
    userBlocks,
    masterBlocks,
    commonBlocksByCategories,
    filterBySearchValue,
    isTemplate,
  ])

  const removeBlock = useCallback(
    (block: BlockType) => {
      if (block.isMaster) {
        deleteMasterBlockAction(block.id)
      } else if (block.category !== blockCategories.user) {
        deleteUserBlockAction(block.id, rollbar)
      } else {
        removeCommonBlock(block.id, blockCategories, rollbar)
      }
    },
    [
      rollbar,
      deleteMasterBlockAction,
      deleteUserBlockAction,
      removeCommonBlock,
    ],
  )

  const isBlockDisabled = useCallback(
    (block: BlockType) => {
      if (block.isMaster && isMasterBlockInUse(block)) {
        return true
      } else {
        return block.content.some(
          entity => !allowedPageEntities.includes(entity.type),
        )
      }
    },
    [isMasterBlockInUse, allowedPageEntities],
  )

  return (
    <>
      <BackButton className="mb-5!" onClick={() => setActiveCategory(null)}>
        {t('components.core.blocks.back_to_groups')}
      </BackButton>
      {isCustomizableCategory && (
        <>
          <input
            type="search"
            placeholder={t('components.core.blocks.search.placeholder')}
            onChange={e => setSearchValue(e.target.value)}
            className="mt-1 mb-0.5 h-9 w-full w-full rounded-md border border-inputBorder border-solid p-1 indent-1 text-S shadow-[inset_1px_1px_1px_rgba(130,137,150,0.23)] [&::-webkit-search-cancel-button]:cursor-pointer"
          />
          <div className="h-full overflow-auto overflow-x-hidden p-0">
            {renderedBlocks.map(block => {
              return (
                <Block
                  key={block.id}
                  block={block}
                  remove={() => removeBlock(block)}
                  isDisabled={isBlockDisabled(block)}
                  isBlockInUse={isMasterBlockInUse(block)}
                  addToTheBottom={addToTheBottom}
                  isRemoveAllowed
                  isSharingAllowed
                  isMobile={isMobile}
                />
              )
            })}
          </div>
        </>
      )}
      {activeCategory && !isCustomizableCategory && (
        <div className="h-full overflow-auto overflow-x-hidden p-0">
          {renderedBlocks?.map(block => {
            return (
              <Block
                key={block.id}
                block={block}
                isDisabled={isBlockDisabled(block)}
                isBlockInUse={isMasterBlockInUse(block)}
                remove={() => removeBlock(block)}
                addToTheBottom={addToTheBottom}
                isRemoveAllowed={isAdmin}
                isMobile={isMobile}
              />
            )
          })}
        </div>
      )}
    </>
  )
}
